import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Button,
    Stack,
    Chip,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    FilterAltOff as ClearFilterIcon,
    LocalShipping as ShippingIcon,
    Place as PlaceIcon,
    Inventory2 as PackageIcon,
    Send as OutForDeliveryIcon,
    ReportProblem as OverdueIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import apiClient from '../../services/apiClient';
import { transitService } from '../../services/transitService';

const STATUS_OPTIONS = [
    { value: '', label: 'All in-transit' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' }
];

const STATUS_CHIP_COLOR = {
    pending: 'warning',
    picked_up: 'primary',
    in_transit: 'info',
    out_for_delivery: 'warning',
    delivered: 'success'
};

// How recent a package's last vehicle scan must be to count as in transit. Bounding
// this is what keeps the query cost flat as tracking history grows, so it is a real
// filter rather than cosmetic - widening it makes the page measurably slower.
//
// The same number reads in opposite directions on the two views: In Transit shows
// packages scanned WITHIN it, Overdue shows packages NOT scanned for at least that
// long. That is why they are separate routes rather than tabs sharing one control -
// each mounts its own state, so a window chosen while chasing a stuck package cannot
// follow the manager back to the planner and quietly slow it down.
const WINDOW_OPTIONS = [
    { value: 3, activeLabel: 'Last 3 days', overdueLabel: 'More than 3 days' },
    { value: 7, activeLabel: 'Last 7 days', overdueLabel: 'More than 7 days' },
    { value: 30, activeLabel: 'Last 30 days', overdueLabel: 'More than 30 days' },
    { value: 90, activeLabel: 'Last 90 days', overdueLabel: 'More than 90 days' }
];

const EMPTY_FILTERS = { vehicle_id: '', status: '', search: '', dateFrom: '', dateTo: '', windowDays: 3 };

/**
 * Both the Transit Planner and the Overdue lookup. `scope` decides which, and is
 * fixed by the route rather than toggled in the page - see WINDOW_OPTIONS above.
 * Only the mounted scope is ever fetched; Overdue costs nothing until navigated to.
 */
const TransitPlanner = ({ scope = 'active' }) => {
    const navigate = useNavigate();
    const isOverdue = scope === 'overdue';
    const [loading, setLoading] = useState(false);
    const [packages, setPackages] = useState([]);
    const [summary, setSummary] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 15,
        total: 0
    });
    // draft = current input values; filters = applied (drives fetch)
    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    // Load active vehicles once for the filter dropdown
    useEffect(() => {
        const loadVehicles = async () => {
            try {
                const response = await apiClient.get('/vehicles/active');
                if (response.data.success) {
                    setVehicles(response.data.data || []);
                }
            } catch (err) {
                // Non-fatal: the page still works, just without the dropdown list
                console.error('Failed to load vehicles', err);
            }
        };
        loadVehicles();
    }, []);

    const buildParams = useCallback((includePaging) => {
        const params = {
            scope,
            window_days: filters.windowDays,
            vehicle_id: filters.vehicle_id || undefined,
            status: filters.status || undefined,
            search: filters.search || undefined,
            date_from: filters.dateFrom || undefined,
            date_to: filters.dateTo || undefined
        };
        if (includePaging) {
            params.page = pagination.page + 1;
            params.per_page = pagination.rowsPerPage;
        }
        return params;
    }, [scope, filters, pagination.page, pagination.rowsPerPage]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [pkgRes, sumRes] = await Promise.all([
                transitService.getPackages(buildParams(true)),
                transitService.getSummary(buildParams(false))
            ]);

            if (pkgRes.success) {
                setPackages(pkgRes.data || []);
                setPagination(prev => ({ ...prev, total: pkgRes.pagination?.total || 0 }));
            } else {
                setError(pkgRes.message || 'Failed to fetch transit packages');
            }

            if (sumRes.success) {
                setSummary(sumRes.data);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch transit data');
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleApplyFilters = () => {
        setFilters(draft);
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handleResetFilters = () => {
        setDraft(EMPTY_FILTERS);
        setFilters(EMPTY_FILTERS);
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    // Apply a single field immediately (used by summary chips and selects)
    const applyField = (field, value) => {
        setDraft(prev => ({ ...prev, [field]: value }));
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleRowsPerPageChange = (event) => {
        setPagination(prev => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const totals = summary?.totals;
    const windowLabel = isOverdue ? 'Not scanned for' : 'Scanned within';

    const statCards = [
        {
            title: isOverdue ? 'Overdue' : 'In Transit',
            value: totals?.total_packages ?? 0,
            icon: <PackageIcon color="primary" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />,
            valueColor: 'primary',
            chip: { label: 'Packages', color: 'primary' }
        },
        {
            title: 'Vehicles',
            value: totals?.vehicles_in_use ?? 0,
            icon: <ShippingIcon color="info" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />,
            valueColor: 'info.main',
            chip: { label: 'In use', color: 'info' }
        },
        {
            title: 'Destinations',
            value: totals?.destinations ?? 0,
            icon: <PlaceIcon color="success" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />,
            valueColor: 'success.main',
            chip: { label: 'Cities', color: 'success' }
        },
        {
            title: 'Out for Delivery',
            value: totals?.out_for_delivery ?? 0,
            icon: <OutForDeliveryIcon color="warning" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />,
            valueColor: 'warning.main',
            chip: { label: 'On the way', color: 'warning' }
        }
    ];

    return (
        <DashboardLayout>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {isOverdue ? 'Overdue Packages' : 'Transit Planner'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {isOverdue
                                ? 'Undelivered packages that have not been scanned onto a vehicle for longer than the selected period. These have fallen out of the normal transit cycle - held at a hub, mis-scanned, or stuck.'
                                : 'In-transit packages and the vehicle carrying each one. Filter by vehicle to plan deliveries on the way (e.g. hold a van already carrying Chennai-bound packages).'}
                        </Typography>
                    </Box>
                    {isOverdue ? (
                        <Button
                            variant="outlined"
                            startIcon={<BackIcon />}
                            onClick={() => navigate('/employee/transit-planner')}
                            sx={{ flexShrink: 0 }}
                        >
                            Transit Planner
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<OverdueIcon />}
                            onClick={() => navigate('/employee/transit-planner/overdue')}
                            sx={{ flexShrink: 0 }}
                        >
                            Overdue Packages
                        </Button>
                    )}
                </Stack>

                {/* Statistics Cards */}
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: 3 }}>
                    {statCards.map((card) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={card.title}>
                            <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                        {card.icon}
                                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                            {card.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h4"
                                        color={card.valueColor}
                                        gutterBottom
                                        sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                                    >
                                        {card.value}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip
                                            label={card.chip.label}
                                            color={card.chip.color}
                                            size="small"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Vehicle & destination breakdown chips */}
                {summary && (summary.by_vehicle?.length > 0 || summary.by_destination?.length > 0) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <ShippingIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        By vehicle
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {summary.by_vehicle?.length ? summary.by_vehicle.map((v) => (
                                        <Chip
                                            key={`veh-${v.vehicle_id ?? v.vehicle_number}`}
                                            label={`${v.vehicle_number || 'Unassigned'} · ${v.package_count}`}
                                            color={String(filters.vehicle_id) === String(v.vehicle_id) ? 'primary' : 'default'}
                                            variant={String(filters.vehicle_id) === String(v.vehicle_id) ? 'filled' : 'outlined'}
                                            onClick={() => v.vehicle_id && applyField('vehicle_id', String(v.vehicle_id))}
                                            clickable={!!v.vehicle_id}
                                        />
                                    )) : (
                                        <Typography variant="body2" color="text.secondary">No vehicles in transit</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <PlaceIcon fontSize="small" color="error" />
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        By destination
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {summary.by_destination?.length ? summary.by_destination.map((d, idx) => (
                                        <Chip
                                            key={`dest-${d.destination_city_id ?? idx}`}
                                            label={`${d.destination || 'Unknown'} · ${d.package_count}`}
                                            variant="outlined"
                                            onClick={() => d.destination && applyField('search', d.destination)}
                                            clickable={!!d.destination}
                                        />
                                    )) : (
                                        <Typography variant="body2" color="text.secondary">No destinations</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Filters + packages table in one white card (matches Booking/Invoice) */}
                <Paper sx={{ width: '100%' }}>
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h6" component="h2">
                                    {isOverdue ? 'Overdue Packages' : 'Transit Packages'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {pagination.total > 0
                                        ? `Showing ${pagination.page * pagination.rowsPerPage + 1} to ${Math.min((pagination.page + 1) * pagination.rowsPerPage, pagination.total)} of ${pagination.total} packages`
                                        : 'No packages in transit'}
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Filters */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>{windowLabel}</InputLabel>
                                    <Select
                                        value={draft.windowDays}
                                        label={windowLabel}
                                        onChange={(e) => applyField('windowDays', e.target.value)}
                                    >
                                        {WINDOW_OPTIONS.map((w) => (
                                            <MenuItem key={w.value} value={w.value}>
                                                {isOverdue ? w.overdueLabel : w.activeLabel}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Vehicle</InputLabel>
                            <Select
                                value={draft.vehicle_id}
                                label="Vehicle"
                                onChange={(e) => applyField('vehicle_id', e.target.value)}
                            >
                                <MenuItem value="">All Vehicles</MenuItem>
                                {vehicles.map((v) => (
                                    <MenuItem key={v.id} value={String(v.id)}>
                                        {v.vehicle_number}{v.vehicle_type ? ` (${v.vehicle_type})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={draft.status}
                                label="Status"
                                onChange={(e) => applyField('status', e.target.value)}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Booked from"
                            type="date"
                            value={draft.dateFrom}
                            onChange={(e) => setDraft(prev => ({ ...prev, dateFrom: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Booked to"
                            type="date"
                            value={draft.dateTo}
                            onChange={(e) => setDraft(prev => ({ ...prev, dateTo: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            placeholder="Search by AWB, destination, recipient, vehicle..."
                            value={draft.search}
                            onChange={(e) => setDraft(prev => ({ ...prev, search: e.target.value }))}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleApplyFilters();
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'stretch' }}>
                        <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="stretch">
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterIcon />}
                                onClick={handleApplyFilters}
                                disabled={loading}
                                sx={{ height: (theme) => theme.spacing(7), flex: 1 }}
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ClearFilterIcon />}
                                onClick={handleResetFilters}
                                sx={{ height: (theme) => theme.spacing(7), flex: 1 }}
                            >
                                Clear
                            </Button>
                        </Stack>
                    </Grid>
                        </Grid>

                        {/* Packages table */}
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>AWB No</TableCell>
                                    <TableCell>Vehicle</TableCell>
                                    <TableCell>Route</TableCell>
                                    <TableCell>Recipient</TableCell>
                                    <TableCell align="center">Pkgs</TableCell>
                                    <TableCell align="right">Weight</TableCell>
                                    <TableCell>Current location</TableCell>
                                    <TableCell>Last scan</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Booked</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center"><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : packages.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            {isOverdue
                                                ? 'No overdue packages - everything on a vehicle has been scanned within the selected period.'
                                                : 'No in-transit packages found. If a package is missing, check Overdue Packages or widen the scan window.'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    packages.map((p) => (
                                        <TableRow key={p.awb_no} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">{p.awb_no}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <ShippingIcon fontSize="small" color="action" />
                                                    <Box>
                                                        <Typography variant="body2">{p.current_vehicle_number || '-'}</Typography>
                                                        {p.vehicle_type && (
                                                            <Typography variant="caption" color="text.secondary">{p.vehicle_type}</Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{p.origin || '-'}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    → {p.destination || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{p.to_name || '-'}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {[p.to_city, p.to_pincode].filter(Boolean).join(' · ')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">{p.no_of_pack ?? '-'}</TableCell>
                                            <TableCell align="right">
                                                {p.charged_wt ? `${p.charged_wt} kg` : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={p.current_activity || ''}>
                                                    <Typography variant="body2">{p.current_location || '-'}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {p.vehicle_scan_date ? (
                                                    <>
                                                        <Typography variant="body2">
                                                            {format(new Date(p.vehicle_scan_date), 'dd MMM')}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color={isOverdue ? 'error.main' : 'text.secondary'}
                                                        >
                                                            {p.days_since_vehicle_scan === 0
                                                                ? 'today'
                                                                : `${p.days_since_vehicle_scan}d ago`}
                                                        </Typography>
                                                    </>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={p.status_label || p.status}
                                                    color={STATUS_CHIP_COLOR[p.status] || 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {p.booking_date ? format(new Date(p.booking_date), 'dd MMM yyyy') : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page}
                            onPageChange={handlePageChange}
                            rowsPerPage={pagination.rowsPerPage}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                        />
                    </Box>
                </Paper>
            </Box>
        </DashboardLayout>
    );
};

export default TransitPlanner;
